import { useTranslation } from 'react-i18next';

import { DashboardCard, DatabaseIcon, solidItem, useMemoryDocument } from '@nexus/ari-lib';
import { Type, Clock } from '@nexus/ari-lib/components/lucide-icon';
import { useUserSettings } from '@nexus/supabase';

import { Box, Stack, Switch, Typography, CircularProgress } from '@mui/material';

import { getAuthBackendUrl } from '../../config/global';

// ----------------------------------------------------------------------

const MAX_LENGTH = 5000;

type DashboardMemorySectionProps = {
  userId: string | null;
  onClick?: () => void;
};

export function DashboardMemorySection({ userId, onClick }: DashboardMemorySectionProps) {
  const { t } = useTranslation();
  const itemStyle = solidItem;
  const { settings, updateSettings } = useUserSettings({ userId });
  const { text, isLoading, updatedAt } = useMemoryDocument({
    baseUrl: getAuthBackendUrl(),
    userId,
    enabled: !!userId,
  });

  const memoryEnabled = settings?.enable_memory ?? true;
  const charCount = text.length;

  const formattedDate = updatedAt
    ? new Date(updatedAt).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <DashboardCard
      data-testid="cloud.dashboard.memory-section"
      title="Memory"
      subtitle="What Ari knows about you"
      icon={<DatabaseIcon sx={{ width: 28, height: 28 }} />}
      onClick={onClick}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={16} />
        </Box>
      ) : (
        <Stack spacing={1} sx={{ height: '100%' }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              height: 68,
              ...itemStyle,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Memory
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'grey.400',
                  lineHeight: 1.2,
                  display: 'block',
                  mt: 0.25,
                }}
              >
                Allow Ari to learn from your chats
              </Typography>
            </Box>
            <Switch
              slotProps={{ input: { role: 'switch', 'aria-label': t('accessibility.toggleMemory') } }}
              checked={memoryEnabled}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => updateSettings({ enable_memory: e.target.checked })}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1,
            }}
          >
            <Box
              sx={{
                px: 2,
                ...itemStyle,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box sx={{ color: 'text.secondary', opacity: 0.5, lineHeight: 0 }}>
                <Type size={16} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Characters saved
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {charCount.toLocaleString()} / {MAX_LENGTH.toLocaleString()}
              </Typography>
            </Box>

            <Box
              sx={{
                px: 2,
                ...itemStyle,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: 0.5,
              }}
            >
              <Box sx={{ color: 'text.secondary', opacity: 0.5, lineHeight: 0 }}>
                <Clock size={16} />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Last updated
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {formattedDate ?? '—'}
              </Typography>
            </Box>
          </Box>
        </Stack>
      )}
    </DashboardCard>
  );
}
